import { Button, Drawer, Flex, Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { TextInput } from 'react-hook-form-mantine';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { useTranslationOrganization } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { OrganizationRes } from '@dealstack/shared/lib/generated';
import { useUpdateOrganization } from '@dealstack/frontend/app/pages/Organizations/hooks/useUpdateOrganization';
import {
	EDIT_ORGANIZATION_FORM,
	EditOrganizationFormValues,
	EditOrganizationShape,
} from '@dealstack/frontend/app/pages/Organizations/components/EditOrganizationDrawer/types';

interface Props {
	opened: boolean;
	onClose: () => void;
	organization: OrganizationRes | null;
}

export const EditOrganizationDrawer = ({ opened, onClose, organization }: Props) => {
	const tOrganization = useTranslationOrganization();
	const { mutate: updateOrg, isPending } = useUpdateOrganization();

	const form = useForm<EditOrganizationFormValues>({
		mode: 'all',
		resolver: zodResolver(EditOrganizationShape()),
		defaultValues: {
			[EDIT_ORGANIZATION_FORM.NAME]: '',
			[EDIT_ORGANIZATION_FORM.CONTACT_EMAIL]: '',
		},
	});

	useEffect(() => {
		if (opened && organization) {
			form.reset({
				[EDIT_ORGANIZATION_FORM.NAME]: organization.name,
				[EDIT_ORGANIZATION_FORM.CONTACT_EMAIL]: organization.contactEmail ?? '',
			});
		}
	}, [opened, organization, form]);

	const onSubmit = (values: EditOrganizationFormValues) => {
		if (!organization?.id) {
			return;
		}

		updateOrg(
			{
				id: organization.id,
				body: {
					name: values[EDIT_ORGANIZATION_FORM.NAME],
					contactEmail: values[EDIT_ORGANIZATION_FORM.CONTACT_EMAIL] || null,
				},
			},
			{ onSuccess: onClose }
		);
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tOrganization('organizations_management.edit.drawer.title')}
		>
			<FormProvider {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} style={{ height: '100%' }}>
					<Flex direction="column" justify="space-between" h="100%" gap="1rem">
						<Stack gap="md">
							<TextInput
								name={EDIT_ORGANIZATION_FORM.NAME}
								size="md"
								label={tOrganization(
									'organizations_management.edit.drawer.fields.name.label'
								)}
							/>
							<TextInput
								name={EDIT_ORGANIZATION_FORM.CONTACT_EMAIL}
								size="md"
								label={tOrganization(
									'organizations_management.edit.drawer.fields.contact_email.label'
								)}
							/>
						</Stack>
						<Button
							type="submit"
							disabled={
								isPending || !form.formState.isValid || !form.formState.isDirty
							}
						>
							{tOrganization('organizations_management.edit.drawer.submit')}
						</Button>
					</Flex>
				</form>
			</FormProvider>
		</Drawer>
	);
};
